"use client";

import { MedicalScreening } from "medical-screening-lib";
import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LocaleType } from "staysure-component-library/dist/types/commonTypes";

import { getBrandDomain } from "@/app/common-utils/get-brand-domain.utils";
import HelpOverlayButtonMedical from "@/app/shared-components/help-overlay-button/help-overlay-button-medical";
import logoClickURLs from "@/app/shared-components/logo-component/url-config";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import withBrand from "@/components/with-brand";
import { MedicalScreen, RenewalPage } from "@/config/app-constant";
import { VERISK_KEY, VERISK_URL } from "@/constants";
import useFetchQuoteData from "@/hooks/api/quote/use-fetch-quote";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useCurrentLocale } from "@/libs/i18n/utils.client";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { Organiser } from "@/types/Organiser";
import { Traveller } from "@/types/Traveller";
import { VeriskErrorModalContent } from "@/utils/magnolia/magnolia.utils";
import {
  brandVeriskForMedicalScreening,
  fontVeriskForMedicalScreening,
  styleVeriskForMedicalScreening,
} from "@/utils/medical-screen.utils";
import { useGetGenderByTranslatedTitle } from "@/utils/traveller.utils";

export interface HelpOverlayContentType {
  headerText: string;
  bodyText?: string;
  ctaButtonOneType: string;
  ctaLabelOne: string;
  ctaButtonTwoType: string;
  ctaLabelTwo: string;
  ctaButtonOneFunction?: () => void;
  ctaButtonTwoFunction?: () => void;
  ctaButtonThreeFunction?: () => void;
  infoBoxContent: string;
}

export interface DrawerAccordionContentItem {
  id: string;
  title: string;
  content: string;
}

type MedicalAppProps = {
  brand?: string;
  drawerAccordionContents?: DrawerAccordionContentItem[];
  veriskErrorModalContent?: VeriskErrorModalContent;
};

function MedicalApp({
  brand = "staysure",
  drawerAccordionContents,
  veriskErrorModalContent,
}: MedicalAppProps) {
  const [isClient, setIsClient] = useState(false);
  const dispatch = useDispatch();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const [selectedTraveler, setSelectedTraveler] = useState<
    Traveller | null | undefined
  >(null);
  const [selectedOrganizer, setSelectedOrganizer] = useState<
    Organiser | null | undefined
  >(null);

  const [hashCode, setHashCode] = useState<string | null>(null);
  const [medicalEdit, setMedicalEdit] = useState<string | null>(null);
  const [travellerEdit, setTravellerEdit] = useState<string | null>(null);
  const [organizerEdit, setOrganizerEdit] = useState<string | null>(null);
  const [travelerId, setTravelerId] = useState<string | null>(null);
  const { hideLoading, showLoading } = useLoading();
  const { data, error, loading } = useFetchQuoteData("", "");
  const [styleLoadState, setStyleLoadState] = useState<boolean>(false);
  const [isMigrationWarn, setMigrationWarn] = useState<string | boolean | null>(
    ""
  );
  const [veriskUnavailable, setVeriskUnavailable] = useState<boolean>(false);
  const locale = useCurrentLocale();
  const { t } = useWizardTranslation();
  const getGenderByTranslatedTitle = useGetGenderByTranslatedTitle();

  const application = process.env.NEXT_PUBLIC_APPLICATION ?? "quote";

  function setDynamicFonts(primaryFont: string, secondaryFont: string) {
    const root = document.documentElement;
    // Set CSS variables dynamically
    root.style.setProperty("--font-primary", primaryFont);
    root.style.setProperty("--font-secondary", secondaryFont);
  }

  useEffect(() => {
    if (styleVeriskForMedicalScreening.showStyleInitiate) {
      showLoading();
    }

    // load related style file with env brand
    const link = document.createElement("link");
    link.rel = styleVeriskForMedicalScreening.stylesheet;
    link.href = `/${brand}.css?${new Date().getTime()}`; // Dynamically load based on brand
    link.id = styleVeriskForMedicalScreening.id;
    link.onload = () => {
      setStyleLoadState(true); // Delay setting state
    };
    document.head.appendChild(link);
    // load related font according to the brand
    setDynamicFonts(
      fontVeriskForMedicalScreening.primaryFont,
      fontVeriskForMedicalScreening.secondoryFont
    );

    return () => {
      hideLoading();
      if (link) {
        link.onload = null;
        link.onerror = null;
        document.head.removeChild(link);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand]);

  useEffect(() => {
    if (loading) {
      showLoading();
    } else {
      hideLoading();
    }
    if (error) {
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, loading]);

  const validateHashCode = (hash: string | null) => {
    const REGEX_HASH_CODE = "^[a-zA-Z0-9]+$";
    const regex = new RegExp(REGEX_HASH_CODE);
    return hash && regex.test(hash);
  };

  const handleInvalidData = () => {
    // Handle invalid data
    window.open("/not-found", "_self");
  };

  useEffect(() => {
    setIsClient(true);

    // access related query parameters
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const traveller = urlParams.get("travellerId");
    const hash = urlParams.get("hash");
    const medicalEditValue = urlParams.get("medicalEdit");
    const migrationWarn = urlParams.get("showWarning");
    const veriskUnavailableParam = urlParams.get("veriskUnavailable");
    const travellerEditValue = urlParams.get("travellerEdit");
    const organiserEditValue = urlParams.get("organizerEdit");

    if (migrationWarn) {
      setMigrationWarn(migrationWarn);
    }
    if (medicalEditValue) {
      setMedicalEdit(medicalEditValue);
    }
    if (veriskUnavailableParam === "true") {
      setVeriskUnavailable(true);
    }

    if (travellerEditValue) {
      setTravellerEdit(travellerEditValue);
    }

    if (organiserEditValue) {
      setOrganizerEdit(organiserEditValue);
    }

    // Check for unexpected parameters using some
    const hasUnexpectedParams = Array.from(urlParams.keys()).some(
      (key) =>
        ![
          "travellerId",
          "hash",
          "version",
          "medicalEdit",
          "travellerEdit",
          "organizerEdit",
          "showWarning",
          "veriskUnavailable",
          // Allow UTM parameters for marketing tracking
          "utm_source",
          "utm_medium",
          "utm_campaign",
        ].includes(key)
    );

    if (urlParams.keys() && hasUnexpectedParams) {
      handleInvalidData(); // Handle the unexpected parameter
      return; // Exit early if there's an unexpected parameter
    }

    // Validate travellerId and hashCode
    if (hash && !validateHashCode(hash)) {
      handleInvalidData(); // Exit early if validation fails
    }

    // Set state values only if all validations pass
    setTravelerId(traveller);
    setHashCode(hash);
  }, []);

  const { pageReferrer, pageUrl, pageTitle } =
    useCustomPageTracking(MedicalScreen);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  const showCustomMedicalErrorModal =
    productSpecialFeatures?.showCustomErrorModalForMedicalServiceUnavailability;
  const useNewOverlay = productSpecialFeatures?.useNewHelpOverlay;
  useUpdateProgressBar(current?.progressBarPercentage ?? -1);

  // Handle veriskUnavailable error display
  useEffect(() => {
    if (veriskUnavailable) {
      const handlePrimaryCtaClick = () => {
        // Navigate back to medical dashboard
        window.open(
          `/${t("medical_dashboard_path", "medical-dashboard")}`,
          "_self"
        );
      };

      dispatch(
        setApiError({
          httpCode: 500,
          errorCode: "5001",
          errorMessage: "Medical service unavailable",
          errorStatus: true,
          errors: null,
          isCloseIconEnabled: true,
          modalTitle:
            veriskErrorModalContent?.title ??
            "Sorry, medical <br/> screening is unavailable right now",
          modalDescription:
            veriskErrorModalContent?.description ??
            "We can't complete your quote or save your progress right now. This affects both online and phone. Please try again later.",
          ctaPrimaryBtnLabel:
            veriskErrorModalContent?.ctaLabel ?? "Try again later",
          multiButton: false,
          onPrimaryCtaClick: handlePrimaryCtaClick,
          validationEnable: true,
        })
      );
    }
  }, [veriskUnavailable, dispatch, t, veriskErrorModalContent]);

  useEffect(() => {
    if (
      attributes?.travellerDetails ||
      (!attributes.travellerDetails && data?.attributes)
    ) {
      const selectedAttribute =
        (attributes.travellerDetails && attributes) ||
        (data?.attributes ? data?.attributes : attributes);

      const travelers = selectedAttribute.travellerDetails?.traveller;
      setSelectedOrganizer(selectedAttribute?.organiser);
      const decodeTravelerId = decodeURIComponent(travelerId || "");
      const traveler = travelers?.find(
        (item: { encryptedId?: string }) =>
          (item.encryptedId ?? "").trim() === decodeTravelerId
      );
      setSelectedTraveler(traveler);
    }
  }, [attributes, data?.attributes, travelerId]);

  const gender = getGenderByTranslatedTitle(selectedTraveler?.title || "");
  const customerName = selectedTraveler?.firstName
    ? `${selectedTraveler?.firstName} ${selectedTraveler?.lastName}`
    : "";

  const getLogoClickUrl = useCallback(() => {
    const lBrand = getBrandDomain();
    if (application === RenewalPage) {
      return process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!;
    }
    let logoClickUrl = "";
    logoClickURLs.forEach((logoUrl) => {
      if (logoUrl.brand === lBrand) {
        logoClickUrl = logoUrl.logoUrl;
      }
    });
    return logoClickUrl;
  }, [application]);

  return (
    isClient &&
    styleLoadState &&
    customerName && (
      <MedicalScreening
        baseUrl={VERISK_URL}
        brand={brandVeriskForMedicalScreening.v3}
        headerBottomButtonVisibility
        drawerAccordionContents={drawerAccordionContents}
        // call function when complete api call
        onComplete={(hash?: string, complete?: boolean, returnUrl?: string) => {
          sessionStorage.removeItem("medical-libary-style-loaded");

          if (complete) {
            const url = new URL(returnUrl || "");
            const params = new URLSearchParams(url.search);

            // Get the 'travellerId' value
            const travelerIdEncrypted = params.get("travellerId");

            // Build the query parameters
            const queryParams = new URLSearchParams({
              travellerId: travelerIdEncrypted || "",
              HashCode: hash || "",
              version: "10",
            });

            if (medicalEdit) {
              queryParams.append("medicalEdit", "true");
            }
            if (travellerEdit) {
              queryParams.append("travellerEdit", "true");
            }
            if (organizerEdit) {
              queryParams.append("organizerEdit", "true");
            }

            window.open(
              `/${t(
                "medical_dashboard_path",
                "medical-dashboard"
              )}?${queryParams.toString()}`,
              "_self"
            );
          } else {
            window.open(
              `/${t("medical_dashboard_path", "medical-dashboard")}`,
              "_self"
            );
          }
        }}
        // passing data for register api
        customerData={{
          name: customerName,
          postCode: selectedOrganizer?.postCode || "",
          age: selectedTraveler?.age?.toString(),
          quoteOrigin: "BOOKING",
          gender,
          tripDuration: null,
          leadTime: null,
        }}
        logoUrl=""
        // back button in dashboard trigger function
        onBack={() => {
          // back button press on dashboard
          sessionStorage.removeItem("medical-libary-style-loaded");
          const queryParams = new URLSearchParams();
          // Only preserve edit flow parameters

          if (medicalEdit) {
            queryParams.append("medicalEdit", "true");
          }
          if (travellerEdit) {
            queryParams.append("travellerEdit", "true");
          }
          if (organizerEdit) {
            queryParams.append("organizerEdit", "true");
          }
          const queryString = queryParams.toString();
          const url = `/${t("medical_dashboard_path", "medical-dashboard")}${
            queryString ? `?${queryString}` : ""
          }`;

          window.open(url, "_self");
        }}
        externalHelpButton={
          <HelpOverlayButtonMedical useNewHelpOverlay={useNewOverlay} />
        }
        legacyToken={hashCode || ""}
        headerParams={{
          domain: brandVeriskForMedicalScreening.domain,
          operationFlow: "CREATE_FLOW",
          businessFlow: "ONLINE",
          xAPIKey: VERISK_KEY,
        }}
        // if any error from api call this function will call
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError={(error: any) => {
          // this call if any error 500 occur on medical library side
          if (error && error?.data?.statusCode === 500) {
            window.open(
              `/${t("medical_dashboard_path", "medical-dashboard")}`,
              "_self"
            );
          } else if (
            error &&
            error?.data?.statusCode === 5001 &&
            showCustomMedicalErrorModal
          ) {
            setVeriskUnavailable(true);
          }
        }}
        migrationWarn={!!(isMigrationWarn === "true" || isMigrationWarn)}
        headerViewVisibility
        // only pass according to the related app ex: staysure, renewal
        logoClickURL={getLogoClickUrl()}
        isLegacy={brandVeriskForMedicalScreening.domain !== "EXP"}
        language={locale as LocaleType}
      />
    )
  );
}

export default withBrand(MedicalApp);
